import { modelState } from '@/utils/modelState';
import { ReservationPerson, PersonCredit, LoadProfileResult } from './types';
import { isReservationExistingPerson } from "./model.ReservationPerson";
import { CooperativeCancellableLazy } from "../utils/CooperativeCancellableLazy";

// Global cache using Map with personId as key, using CooperativeCancellableLazy pattern
const globalProfileCache = new Map<string, CooperativeCancellableLazy<LoadProfileResult>>();

export function initializeProfileCache(preloadedProfiles: { [personId: string]: ReservationProfileCacheDto }) {
    // Clear existing cache
    globalProfileCache.clear();
    
    // Initialize cache with preloaded profiles
    Object.entries(preloadedProfiles).forEach(([personId, profile]) => {
        const cachedProfile: LoadProfileResult = {
            Credits: profile.Credits.map(c => ({
                CreditId: c.CreditId,
                SequenceNumber: c.SequenceNumber,
                Amount: c.Amount,
                ExpiresOn: new Date(c.ExpiresOn)
            }))
        };
        
        // Create a cooperative cancellable lazy that's already resolved
        const lazy = new CooperativeCancellableLazy((_cancellationToken: AbortController) => Promise.resolve(cachedProfile));
        globalProfileCache.set(personId, lazy);
    });
}

export function cachedPersonProfileLoader(person: modelState<ReservationPerson>) {
    const loading = modelState(false);
    const result: modelState<LoadProfileResult> = modelState(null);

    const subscription = person.subscribe(() => {
        loadProfile(person.value);
    });

    let loadProfileController = new AbortController();

    async function loadProfile(person: ReservationPerson) {
        loadProfileController.abort('cancelled');
        loadProfileController = new AbortController();

        if (!isReservationExistingPerson(person)) {
            result.setValue(null);
            return;
        }

        loading.setValue(true);

        try {
            // Check cache first
            let profileLazy = globalProfileCache.get(person.PersonId);
            
            if (!profileLazy) {
                // Create new lazy loader for this person if not in cache
                profileLazy = new CooperativeCancellableLazy(async (cancellationToken: AbortController) => {
                    const response = await fetch(`/api/reservations/people/${person.PersonId}`, {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json; charset=utf-8'
                        },
                        signal: cancellationToken.signal
                    });
                    
                    if (!response.ok) {
                        throw new Error(`Failed to load profile: ${response.status} ${response.statusText}`);
                    }
                    
                    const personWithCredits = await response.json();
                    // Convert PersonWithCredits to LoadProfileResult (just extract credits)
                    return {
                        Credits: personWithCredits.Credits.map((c: any) => ({
                            CreditId: c.CreditId,
                            Amount: c.Amount,
                            ExpiresOn: new Date(c.ExpiresOn)
                        }))
                    } as LoadProfileResult;
                });
                
                globalProfileCache.set(person.PersonId, profileLazy);
            }

            const profileResult = await profileLazy.getValueAsync(loadProfileController.signal);
            result.setValue(profileResult);
        }
        catch (ex) {
            if (ex === "cancelled" || ex.name === "AbortError") {
                return;
            } else {
                // Remove from cache on error so it can be retried
                globalProfileCache.delete(person.PersonId);
                throw ex;
            }
        }
        finally {
            loading.setValue(false);
        }
    }

    loadProfile(person.value);

    return {
        loading,
        result,
        dispose: subscription
    }
}

export interface ReservationProfileCacheDto {
    PersonId: string;
    Credits: PersonCreditDto[];
}

export interface PersonCreditDto {
    CreditId: string;
    SequenceNumber: number;
    Amount: number;
    ExpiresOn: string;
}